"use client";

import { useMemo, useState } from "react";
import clsx from "clsx";
import { unpackRows, type PackedRows } from "@/lib/screener/transport";
import type { ScanRow, ScanState } from "@/lib/screener/types";
import { DataTable, type Column } from "./data-table";
import {
  FilterBuilder,
  applyRules,
  type FilterField,
  type FilterRule,
} from "./filter-builder";
import {
  CountBadge,
  McapSelect,
  QUADRANT_CLS,
  RsValue,
  SearchBox,
  SelectBox,
  TickerCell,
} from "./controls";
import {
  STATE_ORDER,
  distinct,
  fmt,
  formatMcap,
  matchMcapFilter,
} from "./logic";

export type ScanMode = "pullback" | "breakout";

const STATE_CLS: Record<ScanState, string> = {
  "BUY-BREAKOUT": "bg-up/15 text-up",
  "BUY-PULLBACK": "bg-sky-500/15 text-sky-400",
  "ARMED-EXT": "bg-amber-500/15 text-amber-400",
  "ARMED-WAIT": "bg-surface-2 text-muted",
};

// stockweb page.tsx SIGNALS
const SIGNALS: {
  key: "mo_pos" | "wk_pos" | "day_macd" | "slope_up" | "stack";
  label: string;
  short: string;
  cls: string;
}[] = [
  {
    key: "mo_pos",
    label: "Monthly MACD > 0",
    short: "M",
    cls: "bg-up/15 text-up",
  },
  {
    key: "wk_pos",
    label: "Weekly MACD > 0",
    short: "W",
    cls: "bg-up/15 text-up",
  },
  {
    key: "day_macd",
    label: "Daily MACD > Signal",
    short: "D",
    cls: "bg-up/15 text-up",
  },
  {
    key: "slope_up",
    label: "Histogram slope ↑",
    short: "S↑",
    cls: "bg-purple-500/15 text-purple-300",
  },
  {
    key: "stack",
    label: "EMA stack 9≥20≥50≥200",
    short: "STK",
    cls: "bg-sky-500/15 text-sky-300",
  },
];

// Module-level so the memo deps stay referentially stable.
const ALLOWED: Record<"pullback" | "breakout" | "breakoutAll", ScanState[]> = {
  pullback: ["BUY-PULLBACK"],
  breakout: ["BUY-BREAKOUT"],
  breakoutAll: ["BUY-BREAKOUT", "ARMED-EXT", "ARMED-WAIT"],
};

const BOOL_OPTS = ["true", "false"];

/*
 * stockweb api/screen/route.ts applies `rs >= 70 && rrg_ok` server-side. Here
 * the base holds every GATE row, so the same filter is a removable preset.
 */
const PRESETS: FilterRule[] = [
  { id: "preset-rs", field: "rs", op: "gte", value: "70", preset: true },
  { id: "preset-rrg", field: "rrg_ok", op: "eq", value: "true", preset: true },
];

export function ScannerTab({
  data,
  mode,
}: {
  data: PackedRows<ScanRow>;
  mode: ScanMode;
}) {
  const rows = useMemo(() => unpackRows(data), [data]);
  const [search, setSearch] = useState("");
  const [sectorFilter, setSectorFilter] = useState("All");
  const [quadrantFilter, setQuadrantFilter] = useState("All");
  const [mcapFilter, setMcapFilter] = useState("All");
  // Break out only: stockweb `stateFilter` buy | all (all adds ARMED-*).
  const [showArmed, setShowArmed] = useState(false);
  const [stateBadge, setStateBadge] = useState<ScanState | null>(null);
  const [rrgBadge, setRrgBadge] = useState<"Leading" | "Improving" | null>(
    null,
  );
  const [rules, setRules] = useState<FilterRule[]>(PRESETS);
  const [entries, setEntries] = useState<Record<string, string>>({});
  const [cutlossPct, setCutlossPct] = useState(12);

  const allowed =
    mode === "pullback"
      ? ALLOWED.pullback
      : showArmed
        ? ALLOWED.breakoutAll
        : ALLOWED.breakout;

  // Server order from stockweb: state priority, then Q desc. The client then
  // sorts by Q desc (stable), so ties keep this order.
  const stateRows = useMemo(
    () =>
      rows
        .filter((r) => allowed.includes(r.state))
        .sort(
          (a, b) =>
            STATE_ORDER[a.state] - STATE_ORDER[b.state] ||
            b.q_score - a.q_score,
        ),
    [rows, allowed],
  );

  const sectors = useMemo(
    () => distinct(stateRows.map((r) => r.sector)),
    [stateRows],
  );
  const quadrants = useMemo(
    () => distinct(stateRows.map((r) => r.quadrant)),
    [stateRows],
  );

  const fields = useMemo<FilterField<ScanRow>[]>(
    () => [
      {
        id: "q_score",
        label: "Q score",
        kind: "number",
        get: (r) => r.q_score,
      },
      { id: "rs", label: "RS", kind: "number", get: (r) => r.rs },
      {
        id: "rs_ind",
        label: "RS industry",
        kind: "number",
        get: (r) => r.rs_ind,
      },
      { id: "close", label: "Close", kind: "number", get: (r) => r.close },
      {
        id: "ext_atr",
        label: "Ext (ATR)",
        kind: "number",
        get: (r) => r.ext_atr,
      },
      {
        id: "priority",
        label: "Priority (0–2)",
        kind: "number",
        get: (r) => r.priority,
      },
      {
        id: "mcap_b",
        label: "Market cap ($B)",
        kind: "number",
        get: (r) => (r.market_cap == null ? null : r.market_cap / 1e9),
      },
      { id: "ticker", label: "Ticker", kind: "text", get: (r) => r.ticker },
      {
        id: "sector",
        label: "Sector",
        kind: "enum",
        options: sectors,
        get: (r) => r.sector,
      },
      {
        id: "industry",
        label: "Industry",
        kind: "text",
        get: (r) => r.industry,
      },
      {
        id: "quadrant",
        label: "RRG quadrant",
        kind: "enum",
        options: quadrants,
        get: (r) => r.quadrant,
      },
      {
        id: "state",
        label: "State",
        kind: "enum",
        options: distinct(stateRows.map((r) => r.state)),
        get: (r) => r.state,
      },
      {
        id: "rrg_ok",
        label: "RRG ok",
        kind: "enum",
        options: BOOL_OPTS,
        get: (r) => r.rrg_ok,
      },
      {
        id: "mo_pos",
        label: "Monthly MACD > 0",
        kind: "enum",
        options: BOOL_OPTS,
        get: (r) => r.mo_pos,
      },
      {
        id: "wk_pos",
        label: "Weekly MACD > 0",
        kind: "enum",
        options: BOOL_OPTS,
        get: (r) => r.wk_pos,
      },
      {
        id: "day_macd",
        label: "Daily MACD > Signal",
        kind: "enum",
        options: BOOL_OPTS,
        get: (r) => r.day_macd,
      },
      {
        id: "slope_up",
        label: "Histogram slope ↑",
        kind: "enum",
        options: BOOL_OPTS,
        get: (r) => r.slope_up,
      },
      {
        id: "stack",
        label: "EMA stack",
        kind: "enum",
        options: BOOL_OPTS,
        get: (r) => r.stack,
      },
    ],
    [sectors, quadrants, stateRows],
  );

  // stockweb `baseFiltered` (badge counts are computed from this) + builder rules.
  const baseFiltered = useMemo(() => {
    const s = search.trim();
    return applyRules(stateRows, rules, fields)
      .filter((r) => sectorFilter === "All" || r.sector === sectorFilter)
      .filter((r) => quadrantFilter === "All" || r.quadrant === quadrantFilter)
      .filter((r) => matchMcapFilter(r.market_cap, mcapFilter))
      .filter(
        (r) =>
          !s ||
          r.ticker.includes(s.toUpperCase()) ||
          r.sector?.toLowerCase().includes(s.toLowerCase()),
      );
  }, [
    stateRows,
    rules,
    fields,
    sectorFilter,
    quadrantFilter,
    mcapFilter,
    search,
  ]);

  const filtered = useMemo(
    () =>
      baseFiltered
        .filter((r) => stateBadge === null || r.state === stateBadge)
        .filter((r) => rrgBadge === null || r.quadrant === rrgBadge),
    [baseFiltered, stateBadge, rrgBadge],
  );

  const columns: Column<ScanRow>[] = [
    {
      id: "ticker",
      label: "Ticker",
      pinned: true,
      sortValue: (r) => r.ticker,
      cell: (r) => <TickerCell ticker={r.ticker} />,
    },
    {
      id: "state",
      label: "State",
      sortValue: (r) => r.state,
      cell: (r) => (
        <span
          className={clsx(
            "rounded px-1.5 py-0.5 text-[11px] font-medium",
            STATE_CLS[r.state],
          )}
        >
          {r.state}
        </span>
      ),
    },
    {
      id: "q_score",
      label: "Q",
      align: "center",
      sortValue: (r) => r.q_score,
      cell: (r) => (
        <span className="font-mono font-bold">{fmt(r.q_score, 1)}</span>
      ),
    },
    {
      id: "rs",
      label: "RS",
      align: "center",
      sortValue: (r) => r.rs,
      cell: (r) => <RsValue v={r.rs} hi={90} mid={80} />,
    },
    {
      id: "rs_ind",
      label: "RSi",
      align: "center",
      sortValue: (r) => r.rs_ind,
      cell: (r) => (
        <span className="font-mono text-muted">{r.rs_ind ?? "—"}</span>
      ),
    },
    {
      id: "quadrant",
      label: "RRG",
      align: "center",
      sortValue: (r) => r.quadrant,
      cell: (r) => (
        <span
          className={clsx(
            "text-xs font-medium",
            (r.quadrant && QUADRANT_CLS[r.quadrant]) || "text-muted",
          )}
        >
          {r.quadrant ?? "—"}
        </span>
      ),
    },
    {
      id: "sector",
      label: "Sector",
      sortValue: (r) => r.sector,
      cell: (r) => (
        <div className="max-w-[150px] text-xs" title={r.sector ?? undefined}>
          <div className="truncate font-medium">{r.sector ?? "—"}</div>
          <div className="mt-0.5 font-mono text-[10px] text-muted">
            Cap: {formatMcap(r.market_cap)}
          </div>
        </div>
      ),
    },
    {
      id: "industry",
      label: "Industry",
      defaultHidden: true,
      sortValue: (r) => r.industry,
      cell: (r) => (
        <span className="text-xs text-muted">{r.industry ?? "—"}</span>
      ),
    },
    {
      id: "market_cap",
      label: "Market cap",
      align: "right",
      defaultHidden: true,
      sortValue: (r) => r.market_cap,
      cell: (r) => (
        <span className="font-mono text-xs">{formatMcap(r.market_cap)}</span>
      ),
    },
    {
      id: "close",
      label: "Close",
      align: "right",
      sortValue: (r) => r.close,
      cell: (r) => <span className="font-mono">${fmt(r.close)}</span>,
    },
    {
      id: "ema9",
      label: "EMA9",
      align: "right",
      sortValue: (r) => r.ema9,
      cell: (r) => (
        <span className="font-mono text-amber-300">{fmt(r.ema9)}</span>
      ),
    },
    {
      id: "ema20",
      label: "EMA20",
      align: "right",
      sortValue: (r) => r.ema20,
      cell: (r) => (
        <span className="font-mono text-sky-400">{fmt(r.ema20)}</span>
      ),
    },
    {
      id: "ext_atr",
      label: "Ext ATR",
      align: "right",
      defaultHidden: true,
      title: "(close − EMA9) / ATR14",
      sortValue: (r) => r.ext_atr,
      cell: (r) => <span className="font-mono text-xs">{fmt(r.ext_atr)}</span>,
    },
    {
      id: "entry",
      label: "Entry",
      align: "center",
      cell: (r) => (
        <input
          type="number"
          step="0.01"
          placeholder={fmt(r.close)}
          value={entries[r.ticker] ?? ""}
          onChange={(e) =>
            setEntries((prev) => ({ ...prev, [r.ticker]: e.target.value }))
          }
          aria-label={`Entry ${r.ticker}`}
          className="w-20 rounded-md border border-border bg-surface-2 px-1.5 py-0.5 text-center font-mono text-xs focus:border-accent/60 focus:outline-none"
        />
      ),
    },
    {
      id: "cut",
      label: "Cut",
      align: "center",
      header: (
        <span className="inline-flex items-center gap-1">
          Cut
          <input
            type="number"
            min={1}
            max={99}
            step={0.5}
            value={cutlossPct}
            onChange={(e) => setCutlossPct(Number(e.target.value))}
            aria-label="Cut loss %"
            className="w-11 rounded border border-border bg-surface-2 px-1 text-center font-mono text-[11px] normal-case text-amber-300"
          />
          %
        </span>
      ),
      cell: (r) => {
        // stockweb: cut price = entry × (1 − cut%) when a valid entry is typed.
        const entryStr = entries[r.ticker] ?? "";
        const entryVal = entryStr ? parseFloat(entryStr) : NaN;
        const hasEntry = !isNaN(entryVal) && entryVal > 0;
        return (
          <span className="font-mono text-xs text-orange-400">
            {hasEntry
              ? "$" + (entryVal * (1 - cutlossPct / 100)).toFixed(2)
              : "—"}
          </span>
        );
      },
    },
    // stockweb: gap of the typed entry vs EMA9 / EMA20, in %.
    ...(["ema9", "ema20"] as const).map((k) => ({
      id: `vs_${k}`,
      label: k === "ema9" ? "vs EMA9" : "vs EMA20",
      align: "center" as const,
      cell: (r: ScanRow) => {
        const entryStr = entries[r.ticker] ?? "";
        const entryVal = entryStr ? parseFloat(entryStr) : NaN;
        const ema = r[k];
        const v =
          !isNaN(entryVal) && entryVal > 0 && ema
            ? ((entryVal - ema) / ema) * 100
            : NaN;
        return isNaN(v) ? (
          <span className="text-xs text-muted">—</span>
        ) : (
          <span
            className={clsx(
              "font-mono text-xs",
              v >= 0 ? "text-up" : "text-down",
            )}
          >
            {v >= 0 ? "+" : ""}
            {v.toFixed(2)}%
          </span>
        );
      },
    })),
    {
      id: "signals",
      label: "Signals",
      cell: (r) => (
        <div className="flex gap-1">
          {SIGNALS.map(
            (s) =>
              r[s.key] && (
                <span
                  key={s.key}
                  title={s.label}
                  className={clsx(
                    "rounded px-1 py-0.5 text-[10px] font-medium",
                    s.cls,
                  )}
                >
                  {s.short}
                </span>
              ),
          )}
          {r.priority === 2 && (
            <span
              title="Month+Week MACD both positive (strongest trend)"
              className="rounded bg-amber-500/15 px-1 py-0.5 text-[10px] font-medium text-amber-300"
            >
              ★
            </span>
          )}
        </div>
      ),
    },
  ];

  const toggleState = (s: ScanState) =>
    setStateBadge((cur) => (cur === s ? null : s));
  const toggleRrg = (q: "Leading" | "Improving") =>
    setRrgBadge((cur) => (cur === q ? null : q));
  const count = (pred: (r: ScanRow) => boolean) =>
    baseFiltered.filter(pred).length;

  return (
    <div className="flex flex-col gap-4">
      <FilterBuilder
        fields={fields}
        rules={rules}
        onChange={setRules}
        presets={PRESETS}
        quickFilters={
          <>
            {mode === "breakout" && showArmed && (
              <>
                <CountBadge
                  tone="up"
                  active={stateBadge === "BUY-BREAKOUT"}
                  onClick={() => toggleState("BUY-BREAKOUT")}
                >
                  Breakout: {count((r) => r.state === "BUY-BREAKOUT")}
                </CountBadge>
                <CountBadge
                  tone="amber"
                  active={stateBadge === "ARMED-EXT"}
                  onClick={() => toggleState("ARMED-EXT")}
                >
                  Armed-Ext: {count((r) => r.state === "ARMED-EXT")}
                </CountBadge>
                <CountBadge
                  tone="muted"
                  active={stateBadge === "ARMED-WAIT"}
                  onClick={() => toggleState("ARMED-WAIT")}
                >
                  Armed-Wait: {count((r) => r.state === "ARMED-WAIT")}
                </CountBadge>
              </>
            )}
            <CountBadge
              tone="up"
              active={rrgBadge === "Leading"}
              onClick={() => toggleRrg("Leading")}
            >
              Leading: {count((r) => r.quadrant === "Leading")}
            </CountBadge>
            <CountBadge
              tone="sky"
              active={rrgBadge === "Improving"}
              onClick={() => toggleRrg("Improving")}
            >
              Improving: {count((r) => r.quadrant === "Improving")}
            </CountBadge>
          </>
        }
        controls={
          <>
            <SearchBox
              value={search}
              onChange={setSearch}
              placeholder="ค้นหา ticker / sector…"
            />
            <SelectBox
              label="Sector"
              value={sectorFilter}
              onChange={setSectorFilter}
              options={[
                { value: "All", label: "All sectors" },
                ...sectors.map((s) => ({ value: s, label: s })),
              ]}
            />
            <SelectBox
              label="RRG quadrant"
              value={quadrantFilter}
              onChange={setQuadrantFilter}
              options={[
                { value: "All", label: "All quadrants" },
                ...quadrants.map((q) => ({ value: q, label: q })),
              ]}
            />
            <McapSelect value={mcapFilter} onChange={setMcapFilter} />
            {mode === "breakout" && (
              <SelectBox
                label="State"
                value={showArmed ? "all" : "buy"}
                onChange={(v) => {
                  setShowArmed(v === "all");
                  setStateBadge(null);
                }}
                options={[
                  { value: "buy", label: "BUY signals only" },
                  { value: "all", label: "All (incl. ARMED)" },
                ]}
              />
            )}
          </>
        }
      />

      <DataTable
        rows={filtered}
        columns={columns}
        rowKey={(r) => r.ticker}
        defaultSort={{ id: "q_score", asc: false }}
        toolbar={
          <span>
            <span className="font-medium text-foreground">
              {filtered.length}
            </span>{" "}
            หุ้น จาก {stateRows.length} ที่เข้า{" "}
            {mode === "pullback"
              ? "BUY-PULLBACK"
              : showArmed
                ? "BUY-BREAKOUT / ARMED"
                : "BUY-BREAKOUT"}
          </span>
        }
      />
    </div>
  );
}
